import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Heart, Film, Video, ArrowRight, Star, Gift, Calendar, Users, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: "easeOut" },
};

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const [films, setFilms] = useState([]);

  useEffect(() => {
    api.get("/content/testimonials").then((r) => setTestimonials(r.data || []));
    api.get("/content/films").then((r) => setFilms(r.data || []));
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative h-[100vh] min-h-[640px] w-full overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay muted loop playsInline
          poster="https://images.pexels.com/photos/7854107/pexels-photo-7854107.jpeg"
          data-testid="hero-video"
        >
          <source src="https://cdn.coverr.co/videos/coverr-grandparents-with-their-grandson-5182/1080p.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative h-full flex flex-col justify-end pb-32 md:pb-40">
          <div className="max-w-7xl mx-auto px-6 md:px-10 w-full">
            <motion.p
              className="eyebrow text-primary mb-6"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.9 }}
            >
              Sydney &amp; Canberra · Legacy Documentary
            </motion.p>
            <motion.h1
              className="font-serif text-white text-5xl md:text-7xl lg:text-8xl leading-[1.05] max-w-4xl"
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 1 }}
            >
              Preserve your family's<br /><em style={{ color: "#C9A961", fontStyle: "italic" }}>greatest</em> story.
            </motion.h1>
            <motion.p
              className="text-white/85 text-lg md:text-xl mt-8 max-w-2xl leading-relaxed"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.9 }}
            >
              Cinematic legacy documentaries and family history films — so the voices, wisdom and warmth of the people you love are never lost.
            </motion.p>
            <motion.div
              className="mt-10 flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.8 }}
            >
              <Link to="/contact" className="btn-primary" data-testid="hero-book-cta">Book a Free Chat <ArrowRight size={18} /></Link>
              <Link to="/gallery" className="btn-outline text-white border-white/70 hover:bg-white hover:text-secondary" data-testid="hero-watch-cta">
                <Play size={16} /> Watch a Film
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="bg-cream section-pad">
        <motion.div className="max-w-4xl mx-auto px-6 text-center" {...fadeUp}>
          <p className="eyebrow mb-6">Our Belief</p>
          <p className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.35] text-headline">
            Every family holds a library of stories. One day, without warning, those pages close.
            <span style={{ color: "#C9A961" }}> We make sure they are heard, seen, and held forever.</span>
          </p>
        </motion.div>
      </section>

      {/* WHAT WE DO */}
      <section className="section-pad bg-offwhite">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div className="mb-16 text-center" {...fadeUp}>
            <p className="eyebrow mb-3">What We Do</p>
            <h2 className="font-serif text-4xl md:text-5xl">Three ways to hold onto what matters.</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Film, title: "Legacy Documentaries", copy: "A cinematic film about a life — the milestones, the small moments, the wisdom to pass on.", to: "/legacy-films", img: "https://images.pexels.com/photos/7545426/pexels-photo-7545426.jpeg" },
              { icon: Users, title: "Family History Films", copy: "Multi-generational stories that thread grandparents, parents and children into a single portrait.", to: "/legacy-films", img: "https://images.pexels.com/photos/8848784/pexels-photo-8848784.jpeg" },
              { icon: Video, title: "Funeral Livestreaming", copy: "Dignified livestreaming and a full same-day service video — ready in under 24 hours — for services across Sydney & Canberra.", to: "/funeral-services-sydney", img: "https://images.unsplash.com/photo-1438032005730-c779502df39b" },
            ].map((it, i) => (
              <motion.div
                key={it.title}
                className="group bg-white rounded-xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.04)] border border-border-warm"
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.15 }}
                data-testid={`what-we-do-${i}`}
              >
                <div className="h-56 overflow-hidden">
                  <img src={it.img} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-8">
                  <it.icon size={26} style={{ color: "#C9A961" }} className="mb-4" />
                  <h3 className="font-serif text-2xl mb-3">{it.title}</h3>
                  <p className="text-warmgrey mb-6">{it.copy}</p>
                  <Link to={it.to} className="link-underline text-sm uppercase tracking-widest">Learn more <ArrowRight size={14} /></Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SAMPLE FILM */}
      {films[0] && (
        <section className="section-pad bg-secondary text-white">
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <motion.div className="text-center mb-12" {...fadeUp}>
              <p className="eyebrow mb-3">A Recent Film</p>
              <h2 className="font-serif text-4xl md:text-5xl text-white">{films[0].title}</h2>
            </motion.div>
            <motion.div className="rounded-xl overflow-hidden border-2" style={{ borderColor: "#C9A961" }} {...fadeUp}>
              <video className="w-full aspect-video object-cover" controls poster={films[0].thumbnail_url} data-testid="sample-film-video">
                <source src={films[0].video_url} type="video/mp4" />
              </video>
            </motion.div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="section-pad bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div className="mb-16 text-center" {...fadeUp}>
            <p className="eyebrow mb-3">The Process</p>
            <h2 className="font-serif text-4xl md:text-5xl">Unhurried. Warm. Cinematic.</h2>
          </motion.div>
          <div className="grid md:grid-cols-4 gap-8 md:gap-4 relative">
            {[
              { n: "01", t: "Free Chat", d: "A gentle phone call to understand your family and what matters most." },
              { n: "02", t: "Plan Together", d: "We shape the story, the setting, and the questions worth asking." },
              { n: "03", t: "Film with Care", d: "One or more filming sessions in a place that feels like home." },
              { n: "04", t: "Cinematic Edit", d: "A finished film — dignified, moving, and yours to keep forever." },
            ].map((s, i) => (
              <motion.div key={s.n} className="text-center px-4 relative" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.12 }}>
                <div className="font-serif text-6xl mb-4" style={{ color: "#C9A961" }}>{s.n}</div>
                <h3 className="font-serif text-2xl mb-3">{s.t}</h3>
                <p className="text-warmgrey text-sm leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-pad bg-offwhite">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <motion.div className="mb-14 text-center" {...fadeUp}>
            <p className="eyebrow mb-3">In Their Words</p>
            <h2 className="font-serif text-4xl md:text-5xl">Families who trusted us with their story.</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div key={t.id || i}
                className="bg-cream p-8 rounded-xl border border-border-warm"
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`testimonial-${i}`}
              >
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={k} size={16} fill="#C9A961" color="#C9A961" />)}
                </div>
                <p className="font-serif text-lg italic text-headline mb-6 leading-relaxed">"{t.quote}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-border-warm">
                  {t.photo_url && <img src={t.photo_url} alt={t.name} className="w-11 h-11 rounded-full object-cover" />}
                  <div>
                    <p className="font-medium text-sm text-headline">{t.name}</p>
                    <p className="text-xs text-warmgrey-muted">{t.relation}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GIFT BLOCK */}
      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-14 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <img src="https://images.pexels.com/photos/3831156/pexels-photo-3831156.jpeg" alt="An adult daughter embracing her father" className="rounded-xl shadow-lg w-full h-[500px] object-cover" />
          </motion.div>
          <motion.div {...fadeUp}>
            <Gift size={36} style={{ color: "#C9A961" }} className="mb-6" />
            <p className="eyebrow mb-3">Gift a Legacy</p>
            <h2 className="font-serif text-4xl md:text-5xl mb-6">The gift that lasts <em style={{ color: "#C9A961" }}>generations</em>.</h2>
            <p className="text-warmgrey text-lg mb-8 leading-relaxed">
              For Father's Day, Mother's Day, a birthday, Christmas, an anniversary — or simply because there is time. A beautifully-presented gift voucher for a legacy film your family will cherish forever.
            </p>
            <Link to="/gift" className="btn-primary" data-testid="home-gift-cta">Give a Legacy <ArrowRight size={18} /></Link>
          </motion.div>
        </div>
      </section>

      {/* FUNERAL BLOCK */}
      <section className="section-pad bg-secondary text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <motion.div {...fadeUp}>
            <Heart size={36} style={{ color: "#C9A961" }} className="mb-6 mx-auto" />
            <p className="eyebrow mb-4">Funeral Services</p>
            <h2 className="font-serif text-4xl md:text-5xl mb-6 text-white">We're here when you need us.</h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
              Dignified livestreaming and a full same-day service video — ready in under 24 hours — handled with the quiet care your family deserves. Available across Sydney and Canberra, often at short notice.
            </p>
            <Link to="/funeral-services-sydney" className="btn-primary" data-testid="home-funeral-cta">
              <Calendar size={16} /> Enquire Now
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-pad bg-cream text-center">
        <motion.div className="max-w-3xl mx-auto px-6" {...fadeUp}>
          <Sparkles size={28} style={{ color: "#C9A961" }} className="mx-auto mb-6" />
          <h2 className="font-serif text-4xl md:text-5xl mb-6">Let's begin the conversation.</h2>
          <p className="text-warmgrey text-lg mb-10">A gentle, no-obligation 20-minute call to understand your family and what could be possible.</p>
          <Link to="/contact" className="btn-primary" data-testid="home-final-cta">Book a Free Chat <ArrowRight size={18} /></Link>
        </motion.div>
      </section>
    </div>
  );
}
